export type UserDetail = {
  id?: string;
  subId?: string;
  name?: string;
  image?: string;
  gender?: string;
  dateBirth?: string;
  phoneNumber?: string;
  address?: string;
  subscriptionStatus?: string;
  subscriptionPlan?: string;
};

type UserDetailResponse = {
  userDetailById?: UserDetail;
};

export type SubscriptionDetail = {
  subscription_plan?: string;
  next_billing_date?: string;
  status?: string;
  amount?: number;
  start_date?: string;
};

type SubscriptionResponse = {
  success?: boolean;
  data?: SubscriptionDetail;
};

function profileBaseUrl(): string {
  return (process.env.NEXT_PUBLIC_BASE_URL || '').replace(/\/$/, '');
}

export async function fetchUserDetail(accessToken: string): Promise<UserDetail | null> {
  const baseUrl = profileBaseUrl();
  if (!baseUrl || !accessToken) return null;

  try {
    const res = await fetch(`${baseUrl}/api/profile`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as UserDetailResponse;
    return data.userDetailById ?? null;
  } catch {
    return null;
  }
}

export type UpdateUserDetailInput = {
  name?: string;
  phoneNumber?: string;
  gender?: string;
  dateBirth?: string;
  address?: string;
};

export async function updateUserDetail(
  accessToken: string,
  input: UpdateUserDetailInput,
): Promise<UserDetail> {
  const baseUrl = profileBaseUrl();
  if (!baseUrl || !accessToken) {
    throw new Error('Missing API configuration or access token');
  }

  const res = await fetch(`${baseUrl}/api/profile`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    throw new Error('Failed to update profile');
  }

  const data = (await res.json()) as UserDetailResponse;
  return data.userDetailById ?? {};
}

export async function fetchSubscription(
  userId: string,
  accessToken: string,
): Promise<SubscriptionDetail | null> {
  const baseUrl = profileBaseUrl();
  if (!baseUrl || !accessToken || !userId) return null;

  try {
    const res = await fetch(`${baseUrl}/api/subscription/getByUserId`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, token: accessToken }),
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as SubscriptionResponse;
    return data.data ?? null;
  } catch {
    return null;
  }
}
